// The tab row's entries. Each is a generic `{ kind, id, label, href, age }`, so existing
// tickets can join the row later as `kind: 'ticket'` without reworking it
// (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "The tab row").

import { draftHref } from './draft-address.js';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "now", "12m", "3h", "2d": the largest whole unit, never rounded up. */
export function formatAge(ms) {
  if (!(ms >= MINUTE)) return 'now';
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)}m`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h`;
  return `${Math.floor(ms / DAY)}d`;
}

/**
 * The store part of a label: the store number (`#104`), else the store's name, else the
 * organization's, else the caller's. A tab is narrow, and the number is what agents say.
 */
function storeLabel({ location, organization, customer }) {
  if (location?.store_number) return `#${location.store_number}`;
  return location?.name || organization?.name || customer?.name || '';
}

function labelFor(draft) {
  const store = storeLabel(draft);
  const subject = typeof draft.subject === 'string' ? draft.subject.trim() : '';
  if (store && subject) return `${store} · ${subject}`;
  return store || subject || 'New ticket';
}

/**
 * @param {object[]} drafts  `GET /api/ticket-drafts` rows.
 * @param {number|Date} now  The clock, injected for the age.
 * @returns the tabs, oldest draft first (by `created_at`, the id breaking a tie).
 */
export function toTabs(drafts, now) {
  const nowMs = now instanceof Date ? now.getTime() : now;
  const created = (draft) => Date.parse(draft.created_at);
  return [...(drafts ?? [])]
    .sort((a, b) => (created(a) - created(b)) || String(a.id).localeCompare(String(b.id)))
    .map((draft) => {
      const createdMs = created(draft);
      return {
        kind: 'draft',
        id: draft.id,
        label: labelFor(draft),
        href: draftHref(draft.id),
        age: Number.isNaN(createdMs) ? '' : formatAge(nowMs - createdMs),
      };
    });
}
